import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Table,
  Checkbox,
  Space,
  message,
  Alert,
  Divider,
  Typography,
  Tag,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  InfoCircleOutlined,
  CheckOutlined,
  CloseOutlined
} from '@ant-design/icons';
import advertisementAPI, { Advertisement, Student, SMSCalculation } from '../../services/advertisementAPI';

const { TextArea } = Input;
const { Option } = Select;
const { Search } = Input;
const { Text, Title } = Typography;

interface AdvertisementFormProps {
  advertisement?: Advertisement | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const AdvertisementForm: React.FC<AdvertisementFormProps> = ({
  advertisement,
  onSuccess,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  
  // Students data
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  
  // SMS calculation
  const [smsCalculation, setSmsCalculation] = useState<SMSCalculation | null>(null);
  const [smsRules, setSmsRules] = useState<string[]>([]);

  useEffect(() => {
    loadStudents();
    loadSMSRules();
    
    if (advertisement) {
      // Mövcud reklamı redaktə edirik
      form.setFieldsValue({
        title: advertisement.title,
        message_type: advertisement.message_type,
        content: advertisement.content
      });
      
      if (advertisement.recipients_data) {
        const recipientIds = advertisement.recipients_data.map(r => r.id);
        setSelectedStudents(recipientIds);
      }
      
      // SMS hesablamasını yenilə
      if (advertisement.content) {
        calculateSMS(advertisement.content, advertisement.recipients_data?.length || 0);
      }
    }
  }, [advertisement, form]);

  const loadStudents = async () => {
    setStudentsLoading(true);
    try {
      const result = await advertisementAPI.getStudentsForAdvertisement({
        search: studentSearch
      });
      
      if (result.success) {
        setStudents(result.data);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Tələbələri yükləmə xətası');
    } finally {
      setStudentsLoading(false);
    }
  };

  const loadSMSRules = async () => {
    try {
      const result = await advertisementAPI.getSMSRules();
      if (result.success) {
        setSmsRules(result.data.rules);
      }
    } catch (error) {
      console.error('SMS qaydalarını yükləmə xətası:', error);
    }
  };

  const calculateSMS = async (content: string, recipientCount: number) => {
    if (!content.trim()) {
      setSmsCalculation(null);
      return;
    }

    try {
      const result = await advertisementAPI.calculateSMS(content, recipientCount);
      if (result.success) {
        setSmsCalculation(result.data!);
      }
    } catch (error) {
      console.error('SMS hesablama xətası:', error);
    }
  };

  const handleStudentSearch = (value: string) => {
    setStudentSearch(value);
  };

  const handleStudentSelect = (studentId: number, checked: boolean) => {
    let newSelected;
    if (checked) {
      newSelected = [...selectedStudents, studentId];
    } else {
      newSelected = selectedStudents.filter(id => id !== studentId);
    }
    
    setSelectedStudents(newSelected);
    
    // SMS hesablamasını yenilə
    const content = form.getFieldValue('content');
    if (content) {
      calculateSMS(content, newSelected.length);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = students.map(s => s.id);
      setSelectedStudents(allIds);
      
      const content = form.getFieldValue('content');
      if (content) {
        calculateSMS(content, allIds.length);
      }
    } else {
      setSelectedStudents([]);
      setSmsCalculation(null);
    }
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    calculateSMS(content, selectedStudents.length);
  };

  const handleSubmit = async (values: any) => {
    if (selectedStudents.length === 0) {
      message.error('Ən azı bir tələbə seçilməlidir');
      return;
    }

    setLoading(true);
    try {
      const advertisementData = {
        ...values,
        recipient_ids: selectedStudents
      };

      let result;
      if (advertisement?.id) {
        result = await advertisementAPI.updateAdvertisement(advertisement.id, advertisementData);
      } else {
        result = await advertisementAPI.createAdvertisement(advertisementData);
      }

      if (result.success) {
        message.success(result.message);
        onSuccess();
      } else {
        message.error(result.message);
        if (result.errors) {
          console.error('Form xətaları:', result.errors);
        }
      }
    } catch (error) {
      message.error('Saxlama xətası');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, [studentSearch]);

  const studentColumns = [
    {
      title: (
        <Checkbox
          checked={selectedStudents.length === students.length && students.length > 0}
          indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        >
          Hamısını seç
        </Checkbox>
      ),
      dataIndex: 'select',
      key: 'select',
      width: 120,
      render: (_: any, record: Student) => (
        <Checkbox
          checked={selectedStudents.includes(record.id)}
          onChange={(e) => handleStudentSelect(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Ad Soyad',
      key: 'name',
      render: (record: Student) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.full_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.fin_code}</div>
        </div>
      ),
    },
    {
      title: 'Telefon',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (phone: string) => (
        <div>
          {phone ? (
            <Text>{phone}</Text>
          ) : (
            <Text type="danger">Telefon yoxdur</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'student_status',
      key: 'student_status',
      render: (status: string) => (
        <Tag color={status === 'aktiv' ? 'green' : 'default'}>
          {status === 'aktiv' ? 'Aktiv' : status}
        </Tag>
      ),
    },
  ];

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          message_type: 'reklam'
        }}
      >
        <Row gutter={16}>
          {/* Sol tərəf - Form */}
          <Col span={12}>
            <Card title="Reklam Məlumatları" size="small">
              <Form.Item
                name="title"
                label="Başlıq"
                rules={[{ required: true, message: 'Başlıq tələb olunur' }]}
              >
                <Input placeholder="Reklam başlığı" />
              </Form.Item>

              <Form.Item
                name="message_type"
                label="Mesaj növü"
                rules={[{ required: true, message: 'Mesaj növü seçilməlidir' }]}
              >
                <Select>
                  <Option value="reklam">Reklam</Option>
                  <Option value="informativ">İnformativ</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="content"
                label="Mesaj məzmunu"
                rules={[{ required: true, message: 'Mesaj məzmunu tələb olunur' }]}
              >
                <TextArea
                  rows={6}
                  placeholder="Reklamın məzmununu yazın..."
                  onChange={handleContentChange}
                  showCount
                />
              </Form.Item>

              {/* SMS hesablaması */}
              {smsCalculation && (
                <Alert
                  message="SMS Hesablaması"
                  description={
                    <div>
                      <p><strong>Simvol sayı:</strong> {smsCalculation.character_count}</p>
                      <p><strong>SMS sayı (hər alıcı):</strong> {smsCalculation.sms_count_per_recipient}</p>
                      <p><strong>Seçilmiş alıcılar:</strong> {smsCalculation.recipient_count}</p>
                      <p><strong>Ümumi SMS:</strong> {smsCalculation.total_sms_needed}</p>
                      <p style={{ color: '#1890ff' }}>{smsCalculation.cost_info}</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
            </Card>
          </Col>

          {/* Sağ tərəf - SMS Qaydaları */}
          <Col span={12}>
            <Card title="SMS Qaydaları" size="small">
              <Alert
                message="Diqqət!"
                description="Göndəriləcək mesaj sayı müştəri sayına vurularaq SMS balansınızdan çıxılacaq!"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <div style={{ fontSize: '13px' }}>
                {smsRules.map((rule, index) => (
                  <div key={index} style={{ marginBottom: 4 }}>
                    • {rule}
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>

        <Divider />

        {/* Tələbələr seçimi */}
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Tələbələr Seçimi</span>
              <Tag color="blue">{selectedStudents.length} seçilmiş</Tag>
            </div>
          }
          size="small"
        >
          <div style={{ marginBottom: 16 }}>
            <Search
              placeholder="Tələbə axtar (ad, soyad, FIN kod)"
              allowClear
              onSearch={handleStudentSearch}
              style={{ width: 300 }}
            />
          </div>

          <Table
            columns={studentColumns}
            dataSource={students}
            rowKey="id"
            loading={studentsLoading}
            size="small"
            pagination={{
              pageSize: 5,
              showSizeChanger: false,
              showQuickJumper: false,
            }}
            scroll={{ y: 300 }}
          />
        </Card>

        {/* Düymələr */}
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              Ləğv et
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={selectedStudents.length === 0}
            >
              {advertisement ? 'Yenilə' : 'Yarat'}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};

export default AdvertisementForm;